// Pure builders + validation for chat navigation intents. Kept free of
// React / store / pi-ai imports so the branch matrix is unit-testable
// (navIntent.test.js) and so a hallucinated scope from the agent can't
// strand the user on an empty view — a bad intent fails closed with a
// message the agent can act on, rather than opening a dead tab.

import { isNavigableScopeDim, navigableScopeDims } from "./analyticsClient.js";
import { GRAPH_CLAIM_PARAM, GRAPH_FOCUS_PARAM } from "./traceGraphOverlay.js";
import { quoteValueIfNeeded } from "./queryLang.js";
import { TURN_FOCUS_PARAM } from "./traceTurnFocus.js";

// Normalize a single scope step's ids: accept an `ids` array or a lone
// `id`, keep only non-empty strings. Returns [] when nothing usable.
function stepIds(step) {
  if (Array.isArray(step?.ids)) {
    return step.ids.filter((x) => typeof x === "string" && x.length > 0);
  }
  if (typeof step?.id === "string" && step.id.length > 0) return [step.id];
  return [];
}

// Validate a scope path the agent wants to open in Explore.
// Returns { ok: true, scopePath: [{dim, ids}] } or { ok: false, error }.
// Every dim must be server-navigable (a real filter), and every step
// must carry at least one id — the ids come from the agent's own
// analytics_query results grouped by that dim.
export function validateExploreScope(scopePath) {
  if (!Array.isArray(scopePath) || scopePath.length === 0) {
    return { ok: false, error: "scopePath must be a non-empty array of {dim, ids} steps." };
  }
  const clean = [];
  for (const step of scopePath) {
    if (!step || typeof step !== "object") {
      return { ok: false, error: "each scope step must be an object {dim, ids}." };
    }
    const { dim } = step;
    if (!isNavigableScopeDim(dim)) {
      return {
        ok: false,
        error: `dim "${dim}" is not navigable. Use one of: ${navigableScopeDims().join(", ")}.`,
      };
    }
    const ids = stepIds(step);
    if (ids.length === 0) {
      return {
        ok: false,
        error: `scope step "${dim}" needs at least one id — pass ids you got from an analytics_query grouped by ${dim} (do not invent ids).`,
      };
    }
    clean.push({ dim, ids });
  }
  return { ok: true, scopePath: clean };
}

// Hybrid-autonomy decision: should a navigation be APPLIED directly
// (vs surfaced as a one-click offer)? Direct only when the user
// explicitly asked AND the chat is docked beside the main column — a
// floating overlay always offers so it never yanks the page out from
// behind itself.
export function shouldNavigateDirectly(requested, docked) {
  return Boolean(requested) && Boolean(docked);
}

export function navStatusBlocks(status) {
  return status !== "active" && status !== "exists";
}

// Human label for a scope path, for the confirmation/affordance copy.
// e.g. [{dim:"endpoints", ids:["a","b"]}] → "2 endpoints".
export function describeScope(scopePath) {
  if (!Array.isArray(scopePath) || scopePath.length === 0) return "Investigation";
  return scopePath
    .map((s) => {
      const n = Array.isArray(s.ids) ? s.ids.length : 0;
      const dim = s.dim === "sessions" ? "traces" : s.dim;
      return n === 1 ? `1 ${dim.replace(/s$/, "")}` : `${n} ${dim}`;
    })
    .join(" › ");
}

// Turn explicit scope navigation into the existing Workspace search language.
// Endpoint IDs use a dedicated exact field, never the hostname search field.
export function workspaceQueryForScope(scopePath) {
    const fields = { endpoints: "endpoint_id", agents: "agent", sessions: "session", clusters: "topic", models: "model", tools: "tool", skills: "skill", mcp_servers: "mcp_server", memories: "memory", identities: "identity", groups: "group" };
    if (!Array.isArray(scopePath) || !scopePath.length) throw new Error("A selection is required.");
    const q = scopePath.map((step) => {
        const field = Object.hasOwn(fields, step?.dim) ? fields[step.dim] : null;
        const ids = stepIds(step);
        if (!field || !ids.length) throw new Error("This selection is not supported in Investigations.");
        return `${field}:${ids.map(quoteValueIfNeeded).join(",")}`;
    }).join(" ");
    // Workspace search cards retain at most 500 characters. Refuse truncation.
    if (q.length > 500) throw new Error("This selection is too large for an investigation search.");
    return q;
}

// Keep the reading and chat graph when moving a Prompt Session to Workspace.
export function applyTraceNavParams(next, { reading = "lanes", graph = null, claim = null } = {}) {
    next.set("mode", "workspace");
    for (const key of ["adminView", "settingsView", "menu", "kernel", "canvas", "tabs", "scope", "active", "view", "xcol", "pin", TURN_FOCUS_PARAM]) next.delete(key);
    if (reading && reading !== "turns") next.set("hist", reading);
    else next.delete("hist");
    if (graph) next.set(GRAPH_FOCUS_PARAM, graph);
    else next.delete(GRAPH_FOCUS_PARAM);
    if (graph && claim) next.set(GRAPH_CLAIM_PARAM, claim);
    else next.delete(GRAPH_CLAIM_PARAM);
    return next;
}
